import { OnaLettermark } from "@/assets/icons/OnaLettermark";
import { Button } from "@/components/flexkit/Button";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useLogout } from "@/queries/user-queries";
import { type FC } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

export const MagiclinkPage: FC = () => {
    useDocumentTitle("Magiclink");
    const navigate = useNavigate();
    const location = useLocation();
    const error = (location.search && new URLSearchParams(location.search).get("error")) ?? "Unknown error";

    const logout = useLogout();

    if (!error) {
        return <Navigate to="/" state={{ from: location }} replace />;
    }

    const isExpired = error === "magiclink_expired";
    const isAlreadySignedIn = error === "already_signed_in";

    let title = isExpired ? "Link expired" : error;
    let description = isExpired ? "The link you clicked on is expired" : "";

    if (isAlreadySignedIn) {
        title = "Already signed in";
        description = "You are already signed in to Ona. Please log out first before using a magic link to sign in.";
    }

    const handleLogout = async () => {
        await logout.mutateAsync();
        navigate("/");
    };

    return (
        <div
            data-testid="magiclink-page"
            className="flex h-full flex-col items-center justify-between gap-4 overflow-visible p-6 sm:overflow-y-auto"
        >
            <div className="my-auto flex max-w-[368px] flex-col items-center gap-7">
                <OnaLettermark className="mb-4 size-12" />
                <div className="flex flex-col items-center gap-2">
                    <Heading1 className="text-center text-2xl font-medium tracking-tight text-content-primary">
                        {title}
                    </Heading1>
                    <Text className="max-w-xs text-center text-content-secondary">{description}</Text>
                </div>
                {isAlreadySignedIn ? (
                    <div className="flex flex-col gap-4">
                        <Button
                            variant="primary"
                            onClick={handleLogout}
                            loading={logout.isPending}
                            data-tracking-id="log-out-magiclink-page"
                        >
                            Log out
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={() => navigate("/")}
                            data-tracking-id="back-to-ona-signed-in-magiclink-page"
                        >
                            Back to Ona
                        </Button>
                    </div>
                ) : (
                    <Button
                        variant="primary"
                        onClick={() => navigate("/")}
                        data-tracking-id="back-to-ona-not-signed-in-magiclink-page"
                    >
                        Back to Ona
                    </Button>
                )}
            </div>
        </div>
    );
};
